import { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import {
  LuArrowRight,
  LuCheck,
  LuCircleHelp,
  LuLogOut,
  LuMessageCircle,
  LuMoon,
  LuPackage,
  LuSparkles,
  LuSun,
  LuUser,
  LuUsers,
} from 'react-icons/lu';
import { AuthContext } from '../../../Context/authContext';
import { THEMES, useTheme } from '../../../theme';
import ProfilePic from '../../../assets/uploadIcon.svg';
import { ACTIVE_WORKSPACE_COOKIE, calculatePercentage, handleCreditsNames, setCookie } from '../../../helpers/common';
import ModalContainer from '../../molecules/ModalContainer';
import PricingSection from '../Pricing/PricingSection';
import useClickOutside from '../../../hooks/useClickOutside';
import { AvatarButton, StyledProfileMenu } from './TopBar.styles';
import LocalOnly from '../../../helpers/placeholders';

const HELP_CENTER_URL = 'https://intercom.help/houdiny-ai/en';

/**
 * The credits shown here, in this order: AI Home, then lead credits (one per
 * lead the AI finds and adds). The other credit types stay internal.
 */
const SHOWN_CREDIT_TYPES = ['ai_home', 'linkedin_scraper'];

/** Balances are stored as decimals ("500.0000"): show 500, or 10.9, never trailing zeros. */
const formatCredit = value => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0';
  return (Math.round(amount * 100) / 100).toLocaleString();
};

const toPercent = (remaining, total) => {
  const pct = calculatePercentage(remaining, total);
  if (pct === '---' || Number.isNaN(Number(pct))) return 0;
  return Math.max(0, Math.min(100, Number(pct)));
};

/** Two-up Light/Dark switch, same shape as the reference's ThemeButton grid. */
const ThemeButton = ({ active, onClick, icon: Icon, label }) => (
  <button type="button" className={`theme-btn${active ? ' active' : ''}`} onClick={onClick} aria-pressed={active}>
    <Icon size={16} />
    {label}
  </button>
);

const ProfileMenu = () => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  const [lang, setLang] = useState('en');

  const { user, onLogout, allowedPages, workspaces, selectedWorkspace } = useContextHook(AuthContext, v => ({
    user: v.user,
    onLogout: v.onLogout,
    allowedPages: v.allowedPages,
    workspaces: v.workspaces,
    selectedWorkspace: v.selectedWorkspace,
  }));

  useClickOutside(
    wrapRef,
    useCallback(() => setOpen(false), []),
  );

  const userData = user?.data;
  const subscription = userData?.subscriptionDetail;
  const stripeSubscriptionId = subscription?.stripeSubscriptionId;
  const hasManualSubscription =
    typeof stripeSubscriptionId === 'string' && stripeSubscriptionId.startsWith('manual_subscription');

  const planName = subscription?.currentPlan ? String(subscription.currentPlan).replace('Plan', '').trim() : 'Free';
  const isPlanActive = subscription?.status === 'active';

  const credits = SHOWN_CREDIT_TYPES.map(type =>
    (userData?.userCredits || []).find(item => item?.creditType === type),
  ).filter(Boolean);

  const canSeeOffers = !allowedPages?.length || allowedPages.includes('/offers');

  const go = to => {
    setOpen(false);
    navigate(to);
  };

  const workspaceList = Array.isArray(workspaces) ? workspaces : [];

  /**
   * Reference: the popover's "workspace · role" line under the email.
   *
   * The role shown is companyRole — the one picked in Settings' Change name
   * dialog — falling back to the workspace membership role when it is unset.
   * Either half can be missing, so the line is joined from whatever exists
   * rather than gated on the workspace.
   */
  const contextRole =
    userData?.companyRole || (selectedWorkspace?.myRole ? String(selectedWorkspace.myRole).replace(/_/g, ' ') : '');
  const contextLine = [selectedWorkspace?.name, contextRole].filter(Boolean).join(' · ');

  const switchWorkspace = workspace => {
    if (!workspace || workspace.id === selectedWorkspace?.id) return;
    // Only the cookie, then reload: updating the selected workspace first made this
    // page start loading the new workspace (skeletons) just before the reload.
    setCookie(ACTIVE_WORKSPACE_COOKIE, workspace.id);
    // A reload is the one guaranteed way every screen's already-fetched data
    // (campaigns, leads, accounts — all scoped by the backend to whichever
    // workspace the request names) gets replaced rather than left stale,
    // since nothing here tracks every hook that would otherwise need to
    // refetch on a workspace change.
    window.location.reload();
  };

  /* Intercom is booted in App.js, so Contact Support opens the messenger the
     app already ships rather than introducing another channel. */
  const contactSupport = () => {
    setOpen(false);
    if (window.Intercom) window.Intercom('show');
    else window.open(HELP_CENTER_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <StyledProfileMenu ref={wrapRef}>
      <AvatarButton
        type="button"
        onClick={() => setOpen(prev => !prev)}
        aria-label="Open profile menu"
        aria-expanded={open}>
        {userData?.profilePicture ? (
          <img src={userData.profilePicture} alt="" onError={e => (e.currentTarget.src = ProfilePic)} />
        ) : null}
      </AvatarButton>

      {open && (
        <div className="profile-popover" role="dialog" aria-label="Profile menu">
          <div className="popover-head">
            <div className="user-name">{userData?.fullName || 'Your account'}</div>
            <div className="user-email">{userData?.email || '—'}</div>
            {contextLine && <div className="user-context">{contextLine}</div>}
            {/* Parked — the plan line ("Free • Inactive").

                It reads as a warning about the account rather than a fact about
                billing: "Inactive" sitting under your own name looks like access
                is about to stop, when all it reflects is the absence of a Stripe
                subscription. `planName` and `isPlanActive` above still compute
                it — unwrap to restore.

            <div className="plan-row">
              <span className="plan-name">{planName}</span>
              <span className="plan-sep">•</span>
              <span className={`plan-status${isPlanActive ? '' : ' inactive'}`}>
                {isPlanActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            */}
          </div>

          {credits.length > 0 && (
            <>
              <div className="popover-divider" />
              <div className="credits-block">
                <div className="credits-title">AI Credits</div>
                {credits.map(item => (
                  <div className="credit-row" key={item?.creditType}>
                    <div className="credit-label">
                      <span>{handleCreditsNames(item?.creditType)}</span>
                      <span>
                        {formatCredit(item?.remaining)}/{formatCredit(item?.total)}
                      </span>
                    </div>
                    <div className="credit-track">
                      <div className="credit-fill" style={{ width: `${toPercent(item?.remaining, item?.total)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {workspaceList.length > 0 && (
            <>
              <div className="popover-divider" />
              <div className="workspace-block">
                <div className="workspace-title">Workspace</div>
                <div className="workspace-list">
                  {workspaceList.map(workspace => {
                    const active = workspace.id === selectedWorkspace?.id;
                    return (
                      <button
                        key={workspace.id}
                        type="button"
                        className={`workspace-item${active ? ' active' : ''}`}
                        onClick={() => switchWorkspace(workspace)}>
                        <span>{workspace.name || 'Workspace'}</span>
                        {active && <LuCheck size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div className="popover-divider" />

          <nav className="popover-nav">
            <button type="button" className="popover-item" onClick={() => go('/profile')}>
              <LuUser size={18} />
              <span>Settings</span>
            </button>
            {allowedPages?.includes('/teams') && !hasManualSubscription && (
              <button type="button" className="popover-item" onClick={() => go('/teams')}>
                <LuUsers size={18} />
                <span>Team &amp; Workspaces</span>
              </button>
            )}
            {/* The reference lists Offers here too. `/offers` carries the
                offers.view permission row, so an empty allowedPages (which
                RouteGuard treats as allow-all) still shows it. */}
            {canSeeOffers && (
              <button type="button" className="popover-item" onClick={() => go('/offers')}>
                <LuPackage size={18} />
                <span>Offers</span>
              </button>
            )}
            <button type="button" className="popover-item" onClick={contactSupport}>
              <LuMessageCircle size={18} />
              <span>Contact Support</span>
            </button>
            <Link
              className="popover-item"
              to={HELP_CENTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}>
              <LuCircleHelp size={18} />
              <span>Help &amp; Tips</span>
            </Link>
          </nav>

          <div className="popover-divider" />

          <div className="theme-block">
            <div className="theme-title">Theme</div>
            <div className="theme-switch">
              <ThemeButton
                active={theme === THEMES.LIGHT}
                onClick={() => setTheme(THEMES.LIGHT)}
                icon={LuSun}
                label="Light"
              />
              <ThemeButton
                active={theme === THEMES.DARK}
                onClick={() => setTheme(THEMES.DARK)}
                icon={LuMoon}
                label="Dark"
              />
            </div>
          </div>

          <div className="popover-divider" />

          <div className="popover-foot">
            <div className="popover-foot-row">
              <button type="button" className="logout-btn" onClick={onLogout}>
                <LuLogOut size={16} />
                Logout
              </button>

              {/* Reference: the footer's language switch. The app is not localised,
                  so picking a flag only recorded a preference nothing reads — it
                  changed the highlight and not a word on screen, which reads as a
                  setting that failed. Shown, because the reference shows it, but
                  inert until there is a second language to switch to. */}
              <LocalOnly>
                <div className="lang-switch">
                  <button
                    type="button"
                    className={lang === 'en' ? 'active' : undefined}
                    aria-label="English"
                    aria-pressed={lang === 'en'}
                    data-coming-soon="above"
                    aria-disabled="true"
                    title="Coming soon"
                    onClick={undefined}>
                    🇺🇸
                  </button>
                  <button
                    type="button"
                    className={lang === 'de' ? 'active' : undefined}
                    aria-label="Deutsch"
                    aria-pressed={lang === 'de'}
                    data-coming-soon="above"
                    aria-disabled="true"
                    title="Coming soon"
                    onClick={undefined}>
                    🇩🇪
                  </button>
                </div>
              </LocalOnly>
            </div>
          </div>

          {/* Parked — the "Scale your AI workforce" upgrade card.

              The pricing modal, its permission gate and the manual-subscription
              exclusion are all untouched underneath; only the card that opens it
              is hidden. Unwrap to restore — nothing else has to change.

          {allowedPages?.includes('/subscription') && !hasManualSubscription && (
            <div className="upgrade-card">
              <ModalContainer
                newStyle
                width={1200}
                btnComponent={({ onClick }) => (
                  <div className="upgrade-inner">
                    <span className="upgrade-icon">
                      <LuSparkles size={20} />
                    </span>
                    <div>
                      <div className="upgrade-title">Scale your AI workforce</div>
                      <button type="button" className="upgrade-btn" onClick={onClick}>
                        <span className="upgrade-btn-text">Upgrade plan</span>
                        <LuArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
                content={({ onClose }) => <PricingSection onClose={onClose} />}
              />
            </div>
          )}
          */}
        </div>
      )}
    </StyledProfileMenu>
  );
};

export default ProfileMenu;
